import React, { FC, useState, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";

import { FileRejection } from "react-dropzone/.";
import { toast } from "react-toastify";

import bulkInviteSample from "~/assets/bulk_invite_sample.csv";

import { parseCSV } from "~/utils";

import { useApisManagersBulkInvitesCreate, useBoolean } from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
} from "~/components/layouts";

import {
  Button,
  DownloadLink,
  DropzoneFileFieldWithFile,
  EmployeeCodeRuleMessage,
  LinkButton,
  PasswordRuleMessage,
} from "~/ui";
import { LabelWithCheckBox } from "~/ui";

// 部署名	課名	メールアドレス	初期パスワード	名前(姓)	名前(名)	カナ(姓)	カナ(名)	ログインID	職種	権限	役職
const HEADERS = [
  "部署名",
  "課名",
  "メールアドレス",
  "初期パスワード",
  "名前(姓)",
  "名前(名)",
  "カナ(姓)",
  "カナ(名)",
  "ログインID",
  "職種",
  "権限",
  "役職",
] as const;

type HeaderType = (typeof HEADERS)[number];

type ValidationRule = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: string) => boolean;
};

type ValidationRules = {
  [key in HeaderType]: ValidationRule;
};

const VALIDATION_RULES: ValidationRules = {
  部署名: {
    required: true,
  },
  課名: {
    required: true,
  },
  メールアドレス: {
    required: false,
  },
  // 英字と数字を1つ以上含む
  初期パスワード: {
    minLength: 8,
    maxLength: 255,
    pattern: /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]{8,}$/,
  },
  "名前(姓)": {
    required: true,
    maxLength: 255,
  },
  "名前(名)": {
    required: true,
    maxLength: 255,
  },
  "カナ(姓)": {
    maxLength: 255,
    pattern: /^[ァ-ヶー]+$/,
  },
  "カナ(名)": {
    maxLength: 255,
    pattern: /^[ァ-ヶー]+$/,
  },
  ログインID: {
    minLength: 4,
    maxLength: 255,
    pattern: /^[a-zA-Z0-9]+$/,
  },
  職種: {
    maxLength: 255,
  },
  権限: {
    maxLength: 255,
  },
  役職: {
    maxLength: 255,
  },
} as const;

type ValidationError = {
  message: string;
  isError: boolean;
};

type ValidationResult = {
  [key in HeaderType]: ValidationError;
};

const getValidationError = (
  value: string,
  rules: ValidationRule,
): ValidationError => {
  if (rules.required && !value) {
    return { message: "必須項目です", isError: true };
  }
  if (value) {
    if (rules.minLength && value.length < rules.minLength) {
      return {
        message: `${rules.minLength}文字以上で入力してください`,
        isError: true,
      };
    }
    if (rules.maxLength && value.length > rules.maxLength) {
      return {
        message: `${rules.maxLength}文字以下で入力してください`,
        isError: true,
      };
    }
    if (rules.pattern) {
      if (!rules.pattern.test(value)) {
        if (rules.pattern.toString().includes("^[ァ-ヶー]+$")) {
          return { message: "カタカナで入力してください", isError: true };
        }
        if (rules.pattern.toString().includes("^[a-zA-Z0-9]+$")) {
          return { message: "半角英数字で入力してください", isError: true };
        }
        if (rules.pattern.toString().includes("(?=.*[a-zA-Z])(?=.*\\d)")) {
          return {
            message: "英字と数字を1つ以上含めてください",
            isError: true,
          };
        }
      }
    }
  }
  return { message: "", isError: false };
};

const validateRow = (row: Record<string, string>): ValidationResult => {
  return Object.entries(VALIDATION_RULES).reduce((acc, [key, rules]) => {
    acc[key as HeaderType] = getValidationError(row[key] || "", rules);
    return acc;
  }, {} as ValidationResult);
};

export const ManagersEmployeeBulkInviteIndex: FC = () => {
  const navigate = useNavigate();

  const [parsedCSV, setParsedCSV] = useState<
    Record<string, string>[] | undefined
  >();
  const { isChecked: isShiftJIS, handleChange: onShiftJisChange } =
    useBoolean(false);

  const [tmpFile, setTmpFile] = useState<File | undefined>(undefined);
  const { mutate: postBulkInvites } = useApisManagersBulkInvitesCreate();

  const handleFileUpload = async (
    acceptedFile: File[],
    fileRejections: FileRejection[],
  ) => {
    if (fileRejections.length) {
      fileRejections.map(({ file, errors }) => {
        if (!errors[0]) return;
        toast.error(`${file.name} - ${errors[0].message}`);
      });
      return;
    }
    const file = acceptedFile[0];
    if (file) {
      setTmpFile(file);
      await handleParseCSV(file, isShiftJIS);
    }
  };

  const handleShiftJISToggle = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!parsedCSV) return;
    if (!tmpFile) return;

    onShiftJisChange(e.currentTarget.checked);
    await handleParseCSV(tmpFile, e.currentTarget.checked);
  };

  const handleParseCSV = async (file: File, isShiftJIS: boolean) => {
    const decoder = isShiftJIS
      ? new TextDecoder("shift-jis")
      : new TextDecoder("utf-8");
    const encodedData = new Uint8Array(await file.arrayBuffer());
    const utf8Text = decoder.decode(encodedData);

    const csvData = parseCSV(utf8Text);
    setParsedCSV(csvData);
  };

  const handleBulkUpload = () => {
    if (!parsedCSV) return;
    const isConfirmed = window.confirm("一括登録を実行しますか？");

    if (!isConfirmed) return;

    const body = parsedCSV.map((row) => ({
      email: row["メールアドレス"] || "",
      lastName: row["名前(姓)"] || "",
      firstName: row["名前(名)"] || "",
      katakanaLastName: row["カナ(姓)"] || "",
      katakanaFirstName: row["カナ(名)"] || "",
      jobTitle: row["職種"] || "",
      employeeRoleJa: row["権限"] || "",
      jobCategoryJa: row["職種"] || "",
      employeeCode: row["ログインID"] || "",
      password: row["初期パスワード"] || "",
      businessDivisions: {
        ja: (row["部署名"] || "").split(",").filter(Boolean),
      },
      businessSections: { ja: (row["課名"] || "").split(",").filter(Boolean) },
    }));

    postBulkInvites(
      { body: body },
      {
        onSuccess: (data) => {
          toast.success(data.message);
          navigate("/managers/employees");
        },
        onError: (error) => {
          toast.error(error.message);
          console.error(error);
        },
      },
    );
  };

  const breadCrumbItems = [
    { label: "ユーザー設定", href: "/managers/employees" },
    { label: "一括登録", href: "/managers/employees" },
  ];

  return (
    <EmployeeLayout withRightSidebar={false} breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar={false}>
        <div className="p-6 space-y-8">
          <div className="flex items-center justify-between">
            {/* ファイル操作ボタン */}
            <DownloadLink
              fileUrl={bulkInviteSample}
              fileName="bulk_invite_sample.csv"
            >
              <Button
                text={"サンプルのCSVをダウンロード"}
                color={"primary"}
                onClick={() => {}}
                outline
              />
            </DownloadLink>
            <div className="flex items-center space-x-4">
              <LinkButton
                text={"非同期Job実行状況を見る"}
                color={"primary"}
                outline
                path="/managers/background_job_statuses"
              />
              <Button
                text={"一括登録を実行する"}
                color={"primary"}
                onClick={handleBulkUpload}
                readonly={!tmpFile}
              />
            </div>
          </div>
          <div className="bg-secondary-100 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-secondary-900">
              ファイル形式をEXCEL→CSVへ変換する方法
            </h4>
            <ol className="mt-2 list-decimal list-inside space-y-2">
              <li>
                Excelでファイルを開いた状態で「ファイル」→「名前を付けて保存」ボタンを選択
              </li>
              <li>
                「ファイルの種類」を「EXCELブック」→「
                <span className="font-semibold">
                  CSV UTF-8(カンマ区切り)(*.csv)
                </span>
                」に変更
              </li>
            </ol>
          </div>
          {parsedCSV && tmpFile && (
            <div className="mt-4">
              もし文字化けが発生する場合は、以下を試してみてください。
              <LabelWithCheckBox
                labelText="Shift-JISで読み込む"
                name="shift-jis"
                checked={isShiftJIS}
                onChange={handleShiftJISToggle}
              />
            </div>
          )}
          <div className="border-secondary-400 border-t border-b space-y-4">
            <div className="p-4">
              <h4 className="text-lg font-semibold text-secondary-900">
                パスワードについて
              </h4>
              <PasswordRuleMessage className="mt-2" />
            </div>
            <div className="p-4">
              <h4 className="text-lg font-semibold text-secondary-900">
                ログインIDについて
              </h4>
              <EmployeeCodeRuleMessage className="mt-2" />
            </div>
          </div>
          <DropzoneFileFieldWithFile
            label="CSVをアップロード"
            acceptFileTypes={{
              "text/csv": [".csv"],
            }}
            fileClassName={
              "w-full cursor-pointer rounded-lg border-dashed border-2 border-gray-300 text-gray-500 flex justify-center items-center py-2 px-4"
            }
            fileName={tmpFile?.name}
            onDropFiles={handleFileUpload}
            noFilesText={"ファイルが選択されていません"}
          />
          {/* CSV内容の表示 */}
          {parsedCSV && parsedCSV.length > 0 && (
            <div className="overflow-x-auto border border-gray-300 rounded-lg p-4 bg-white shadow-md">
              <table className="min-w-full table-auto">
                <thead>
                  <tr>
                    {HEADERS.map((header) => (
                      <th
                        key={header}
                        className="px-4 py-2 text-left text-sm font-medium text-gray-700 bg-gray-100 border-b"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsedCSV.map((row, index) => {
                    const validations = validateRow(row);
                    return (
                      <tr
                        key={index}
                        className={`hover:bg-gray-50 ${
                          index % 2 === 0 ? "bg-gray-50" : "bg-white"
                        }`}
                      >
                        {HEADERS.map((header) => (
                          <td
                            key={header}
                            className={`px-4 py-2 text-sm text-gray-600 border-b relative group ${
                              validations[header].isError ? "bg-red-100" : ""
                            }`}
                          >
                            {row[header]}
                            {validations[header].isError && (
                              <div className="text-red-500">
                                ({validations[header].message})
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </EmployeeLayoutMainContent>
    </EmployeeLayout>
  );
};
